if (!customElements.get('fb-media-library')) {
  class FBMediaLibrary extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.requestStickyUpdate = this.requestStickyUpdate.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.filters = Array.from(this.querySelectorAll('[data-media-filter]'));
      this.dialog = this.querySelector('[data-media-dialog]');
      this.dialogImage = this.querySelector('[data-dialog-image]');
      this.dialogCategory = this.querySelector('[data-dialog-category]');
      this.dialogTitle = this.querySelector('[data-dialog-title]');
      this.dialogCredit = this.querySelector('[data-dialog-credit]');
      this.dialogDownload = this.querySelector('[data-dialog-download]');
      this.dialogPrevious = this.querySelector('[data-dialog-previous]');
      this.dialogNext = this.querySelector('[data-dialog-next]');
      this.dialogMedia = this.querySelector('[data-dialog-media]');
      this.filterStatus = this.querySelector('[data-filter-status]');
      this.boilerplateText = this.querySelector('[data-boilerplate-text]');
      this.copyStatus = this.querySelector('[data-copy-status]');
      this.copyResetTimer = null;
      this.currentItem = null;

      this.addEventListener('click', this.handleClick);

      if (this.dialog) {
        this.dialog.addEventListener('click', this.handleDialogClick);
        this.dialog.addEventListener('close', this.handleDialogClose);
      }

      this.setupStickyFilters();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('scroll', this.requestStickyUpdate);
      window.removeEventListener('resize', this.handleResize);

      if (this.dialog) {
        this.dialog.removeEventListener('click', this.handleDialogClick);
        this.dialog.removeEventListener('close', this.handleDialogClose);
      }
    }

    handleClick(event) {
      const filterButton = event.target.closest('[data-media-filter]');
      if (filterButton) {
        this.applyFilter(filterButton.dataset.mediaFilter, filterButton);
        this.scrollToGallery();
        return;
      }

      const copyButton = event.target.closest('[data-copy-boilerplate]');
      if (copyButton) {
        this.copyBoilerplate(copyButton);
        return;
      }

      const openButton = event.target.closest('[data-media-open]');
      if (openButton) {
        this.openDialog(openButton.closest('[data-media-item]'));
        return;
      }

      if (event.target.closest('[data-dialog-close]')) {
        this.closeDialog();
        return;
      }

      if (event.target.closest('[data-dialog-previous]')) {
        this.moveDialog(-1);
        return;
      }

      if (event.target.closest('[data-dialog-next]')) {
        this.moveDialog(1);
      }
    }

    // The filter bar pins to the top of the viewport for the length of the
    // whole filter group, not just this section. CSS sticky would release it
    // at the end of this section, before the grouped extension sections.
    setupStickyFilters() {
      this.filterBar = this.querySelector('.fb-media-library__filters');
      if (!this.filterBar) return;

      this.filterSpacer = document.createElement('div');
      this.filterSpacer.className = 'fb-media-library__filters-spacer';
      this.filterSpacer.setAttribute('aria-hidden', 'true');
      this.filterBar.before(this.filterSpacer);
      this.stickyFrame = null;

      window.addEventListener('scroll', this.requestStickyUpdate, { passive: true });
      window.addEventListener('resize', this.handleResize);
      this.handleResize();
    }

    get groupLibraries() {
      const group = this.dataset.filterGroup;
      if (!group) return [this];
      return Array.from(
        document.querySelectorAll(`fb-media-library[data-filter-group="${CSS.escape(group)}"]`)
      ).filter((library) => !library.hidden);
    }

    // Where pinned chrome above the bar ends: the sticky press nav, when present.
    get pinnedOffset() {
      const mediaNav = document.querySelector('.fb-media-nav');
      if (!mediaNav) return 0;
      return Math.max(0, mediaNav.getBoundingClientRect().bottom);
    }

    handleResize() {
      if (!this.filterBar) return;

      // Measure the bar in flow so the spacer holds exactly its space.
      const wasStuck = this.filterBar.classList.contains('is-stuck');
      this.filterBar.classList.remove('is-stuck');
      this.filterBar.style.removeProperty('top');
      const styles = getComputedStyle(this.filterBar);
      this.filterBarSpace =
        this.filterBar.offsetHeight + parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
      if (wasStuck) this.filterBar.classList.add('is-stuck');

      this.updateStickyFilters();
    }

    requestStickyUpdate() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        this.updateStickyFilters();
      });
    }

    updateStickyFilters() {
      if (!this.filterBar) return;

      const libraries = this.groupLibraries;
      const groupBottom = libraries[libraries.length - 1].getBoundingClientRect().bottom;
      const barHeight = this.filterBar.offsetHeight;
      const offset = this.pinnedOffset;
      const stuck = this.filterSpacer.getBoundingClientRect().top <= offset && groupBottom > offset;

      this.filterBar.classList.toggle('is-stuck', stuck);
      this.filterSpacer.style.height = stuck ? `${this.filterBarSpace}px` : '';

      // Sit under the press nav, and slide off with the end of the library
      // rather than covering whatever section follows.
      if (stuck) {
        this.filterBar.style.top = `${Math.min(offset, groupBottom - barHeight)}px`;
      } else {
        this.filterBar.style.removeProperty('top');
      }
    }

    // Choosing a filter from the pinned bar brings the start of the results
    // into view, rather than leaving the reader mid-page among hidden tiles.
    scrollToGallery() {
      if (!this.filterBar || !this.filterBar.classList.contains('is-stuck')) {
        this.updateStickyFilters();
        return;
      }

      const top = this.filterSpacer.getBoundingClientRect().top + window.scrollY - this.pinnedOffset;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closeDialog();
    }

    handleDialogClose() {
      document.documentElement.classList.remove('fb-media-dialog-open');

      if (this.dialogImage) {
        this.dialogImage.removeAttribute('src');
        this.dialogImage.removeAttribute('srcset');
      }
    }

    get items() {
      // Collected lazily: sections in the same filter group act as one gallery,
      // and extension sections may parse after this element connects.
      const group = this.dataset.filterGroup;
      if (group) {
        const selector = `fb-media-library[data-filter-group="${CSS.escape(group)}"] [data-media-item]`;
        return Array.from(document.querySelectorAll(selector));
      }
      return Array.from(this.querySelectorAll('[data-media-item]'));
    }

    copyBoilerplate(button) {
      if (!this.boilerplateText) return;

      const text = (this.boilerplateText.innerText || this.boilerplateText.textContent || '').trim();
      if (!text) return;

      this.writeToClipboard(text)
        .then(() => this.confirmCopy(button, true))
        .catch(() => this.confirmCopy(button, false));
    }

    writeToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      }

      return new Promise((resolve, reject) => {
        const field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.top = '-1000px';
        document.body.appendChild(field);
        field.select();

        try {
          const copied = document.execCommand('copy');
          copied ? resolve() : reject(new Error('Copy command was rejected'));
        } catch (error) {
          reject(error);
        } finally {
          document.body.removeChild(field);
        }
      });
    }

    confirmCopy(button, succeeded) {
      const idleLabel = button.dataset.copyLabel || button.textContent;
      const doneLabel = button.dataset.copiedLabel || 'Copied';
      const message = succeeded ? doneLabel : 'Press Ctrl or Cmd + C to copy';

      button.textContent = message;
      button.classList.toggle('is-copied', succeeded);
      if (this.copyStatus) this.copyStatus.textContent = message;

      window.clearTimeout(this.copyResetTimer);
      this.copyResetTimer = window.setTimeout(() => {
        button.textContent = idleLabel;
        button.classList.remove('is-copied');
      }, 2400);

      if (!succeeded && this.boilerplateText) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.boilerplateText);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    applyFilter(category, activeButton) {
      let visibleCount = 0;

      this.items.forEach((item) => {
        const showItem = category === 'all' || item.dataset.mediaCategory === category;
        item.hidden = !showItem;
        if (showItem) visibleCount += 1;
      });

      this.filters.forEach((button) => {
        button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
      });

      // A headed extension section (its own heading, no filter bar) would
      // otherwise sit empty under its heading when another category is picked.
      const group = this.dataset.filterGroup;
      if (group) {
        document
          .querySelectorAll(`fb-media-library[data-filter-group="${CSS.escape(group)}"][data-hide-when-empty]`)
          .forEach((library) => {
            const libraryItems = Array.from(library.querySelectorAll('[data-media-item]'));
            library.hidden = libraryItems.length > 0 && libraryItems.every((item) => item.hidden);
          });
      }

      if (this.currentItem && this.currentItem.hidden) this.closeDialog();

      if (this.filterStatus) {
        const itemLabel = visibleCount === 1 ? 'image' : 'images';
        this.filterStatus.textContent = `${visibleCount} ${itemLabel} shown`;
      }
    }

    openDialog(item) {
      if (!item || !this.dialog) return;

      this.currentItem = item;
      this.updateDialog(item);

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.documentElement.classList.add('fb-media-dialog-open');
    }

    closeDialog() {
      if (!this.dialog || !this.dialog.hasAttribute('open')) return;

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleDialogClose();
      }
    }

    moveDialog(direction) {
      const visibleItems = this.items.filter((item) => {
        return !item.hidden && item.querySelector('[data-media-open]');
      });

      if (visibleItems.length < 2) return;

      const currentIndex = visibleItems.indexOf(this.currentItem);
      const nextIndex = (currentIndex + direction + visibleItems.length) % visibleItems.length;
      this.currentItem = visibleItems[nextIndex];
      this.updateDialog(this.currentItem);
    }

    updateDialog(item) {
      const trigger = item.querySelector('[data-media-open]');
      if (!trigger) return;

      this.dialogImage.src = trigger.dataset.fullSrc || '';
      this.dialogImage.alt = trigger.dataset.alt || '';

      if (this.dialogMedia) {
        const display = trigger.dataset.display || 'photo';
        this.dialogMedia.classList.toggle(
          'fb-media-library-dialog__media--logo-light',
          display === 'logo_light'
        );
        this.dialogMedia.classList.toggle(
          'fb-media-library-dialog__media--logo-dark',
          display === 'logo_dark'
        );
      }

      this.dialogCategory.textContent = trigger.dataset.categoryLabel || '';
      this.dialogTitle.textContent = trigger.dataset.title || '';
      this.dialogCredit.textContent = trigger.dataset.credit || '';
      this.dialogCredit.hidden = !trigger.dataset.credit;

      this.dialogDownload.href = trigger.dataset.downloadHref || trigger.dataset.fullSrc || '#';
      this.dialogDownload.textContent = trigger.dataset.downloadLabel || 'Open high-res';
      this.dialogDownload.hidden = !trigger.dataset.downloadHref && !trigger.dataset.fullSrc;

      const visibleItems = this.items.filter((visibleItem) => {
        return !visibleItem.hidden && visibleItem.querySelector('[data-media-open]');
      });
      const hideNavigation = visibleItems.length < 2;
      this.dialogPrevious.hidden = hideNavigation;
      this.dialogNext.hidden = hideNavigation;
    }
  }

  customElements.define('fb-media-library', FBMediaLibrary);
}
